import React, { useCallback, useEffect, useState } from "react";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import EditIcon from "@material-ui/icons/Edit";
import GetAppIcon from "@material-ui/icons/GetApp";
import IconButton from "@material-ui/core/IconButton";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import PremiumValue from "../../components/PremiumValue";
import SetCelularModal from "../../components/SetCelularModal";
import { ProductIcon, formatCurrency, formatDateOnly, formatPhoneDisplay } from "./helpers";
import SummaryModal from "./SummaryModal";
import EditableField from "./EditableField";
import OccurrencesSection from "./Occurrences/OccurrencesSection";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "#FAFAFA",
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  header: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.25),
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.secondary,
  },
  fonteBadge: {
    fontSize: 11,
    fontStyle: "italic",
    color: theme.palette.text.disabled,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 3,
    padding: "1px 6px",
  },
  rowPrincipal: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  campoPrincipal: {
    flex: "1 1 200px",
    minWidth: 160,
  },
  celularRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    minHeight: 32,
  },
  rowEndereco: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  campoEndereco: {
    flex: "2 1 260px",
    minWidth: 200,
  },
  campoCidade: {
    flex: "1 1 160px",
    minWidth: 140,
  },
  sectionTitle: {
    fontWeight: "bold",
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
  },
  policyRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexWrap: "wrap",
  },
  policyMain: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 220,
  },
  policyDetails: {
    flex: 1,
    display: "flex",
    gap: theme.spacing(2.5),
    flexWrap: "wrap",
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginLeft: "auto",
  },
  noDocument: {
    fontSize: 12,
    fontStyle: "italic",
    color: theme.palette.text.disabled,
    marginLeft: "auto",
  },
  emptySection: {
    fontSize: 13,
    color: theme.palette.text.disabled,
    padding: theme.spacing(0.5, 1),
  },
}));

const PolicyLine = ({ policy, onOpenResumo }) => {
  const classes = useStyles();
  const [opening, setOpening] = useState(null); // "view" | "download" | null

  /**
   * Abrir/baixar o PDF exige um token de curto prazo (GET .../documento-token,
   * autenticado por header via axios) porque o arquivo em si é aberto por
   * navegação direta do navegador (nova aba), que não carrega o header
   * Authorization — só o axios injeta isso nas chamadas internas. Sem o
   * token na URL, /documento sempre respondia ERR_SESSION_EXPIRED.
   *
   * A aba é aberta ANTES do await (clique síncrono) para não ser bloqueada
   * por bloqueador de pop-up; `noopener` não dá pra usar aqui porque
   * precisamos da referência pra setar a URL depois — por isso abre sem
   * noopener e zera `newTab.opener` assim que a URL final é definida.
   */
  const openDocument = async (forceDownload) => {
    const newTab = window.open("", "_blank");
    setOpening(forceDownload ? "download" : "view");
    try {
      const { data } = await api.get(`/carteira/apolice/${policy.id}/documento-token`);
      const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/carteira/apolice/${policy.id}/documento`;
      const url = `${baseUrl}?token=${encodeURIComponent(data.token)}${forceDownload ? "&download=1" : ""}`;
      if (newTab) {
        newTab.location.href = url;
        newTab.opener = null;
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      if (newTab) newTab.close();
      toastError(err);
    } finally {
      setOpening(null);
    }
  };

  return (
    <div className={classes.policyRow}>
      <div className={classes.policyMain}>
        <ProductIcon product={policy.product} fontSize="small" color="action" />
        <Typography variant="body2">
          <strong>{policy.product || "—"}</strong> · {policy.insurer || "—"}
        </Typography>
      </div>

      <div className={classes.policyDetails}>
        <span>Item segurado: {policy.insuredAssetId || "—"}</span>
        {policy.policyNumber ? <span>Apólice {policy.policyNumber}</span> : null}
        <span>
          Vigência: {formatDateOnly(policy.startDate)} a {formatDateOnly(policy.endDate)}
        </span>
        <span>
          Valor Total:{" "}
          <PremiumValue value={policy.premium} source={policy.premiumSource} format={formatCurrency} />
        </span>
      </div>

      {policy.temResumo || policy.temPdf ? (
        <div className={classes.actions}>
          {policy.temResumo ? (
            <Button size="small" variant="outlined" onClick={() => onOpenResumo(policy.id)}>
              Resumo
            </Button>
          ) : null}
          {policy.temPdf ? (
            <>
              <Tooltip title="Ver apólice">
                <span>
                  <IconButton
                    size="small"
                    disabled={Boolean(opening)}
                    onClick={() => openDocument(false)}
                  >
                    {opening === "view" ? (
                      <CircularProgress size={16} />
                    ) : (
                      <OpenInNewIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Baixar apólice">
                <span>
                  <IconButton
                    size="small"
                    disabled={Boolean(opening)}
                    onClick={() => openDocument(true)}
                  >
                    {opening === "download" ? (
                      <CircularProgress size={16} />
                    ) : (
                      <GetAppIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </>
          ) : null}
        </div>
      ) : (
        <span className={classes.noDocument}>sem documento importado</span>
      )}
    </div>
  );
};

/**
 * Painel expandido de um cliente: busca POST /carteira/cliente/consulta só
 * quando a linha abre. `documento` é sempre só dígitos, como devolvido pela
 * lista. Documento vai no body (não mais na URL) para não ficar gravado em
 * access.log/error.log do Nginx.
 */
const ClientDetailPanel = ({ documento }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [resumoAlvo, setResumoAlvo] = useState(null);
  const [celularModalOpen, setCelularModalOpen] = useState(false);

  /**
   * Extraído do useEffect para poder reaproveitar depois que o celular é
   * definido via SetCelularModal (PUT /contatos/celular) — esse endpoint não
   * devolve o detalhe completo da Carteira, então o jeito simples de
   * refletir telefone/fonteContato/etc. atualizados é reconsultar.
   */
  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/carteira/cliente/consulta", { documento });
      setDetail(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [documento]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setDetail(null);
    (async () => {
      try {
        const { data } = await api.post("/carteira/cliente/consulta", { documento });
        if (active) setDetail(data);
      } catch (err) {
        if (active) toastError(err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [documento]);

  if (loading) {
    return (
      <div className={classes.loadingBox}>
        <CircularProgress size={24} />
      </div>
    );
  }

  if (!detail) return null;

  /**
   * PUT /carteira/cliente/contato devolve o mesmo formato de
   * POST /carteira/cliente/consulta — trocar `detail` pela resposta inteira
   * já atualiza telefone/email/cidade/endereco/nascimento e recalcula
   * fonteContato (sem lógica extra aqui: se o campo editado passou a vir do
   * Contact, o backend já resolve sozinho pra "conciliado"). Documento vai no
   * body junto do campo a atualizar, não mais na URL.
   */
  const salvarCampo = async (campo, valor) => {
    const { data } = await api.put("/carteira/cliente/contato", { documento, [campo]: valor });
    setDetail(data);
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        {detail.fonteContato === "planilha_cadastro" ? (
          <span className={classes.fonteBadge}>dado da planilha de cadastro</span>
        ) : null}
        <div className={classes.rowPrincipal}>
          <div className={classes.campoPrincipal}>
            <div className={classes.celularRow}>
              <Typography variant="body2">
                <strong>Celular (WhatsApp):</strong>{" "}
                {detail.telefone ? formatPhoneDisplay(detail.telefone) : "—"}
              </Typography>
              <Tooltip title="Editar celular (WhatsApp)">
                <IconButton size="small" onClick={() => setCelularModalOpen(true)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
          <div className={classes.campoPrincipal}>
            <EditableField
              label="Telefone de contato"
              value={detail.telefoneContato ? formatPhoneDisplay(detail.telefoneContato) : null}
              rawValue={detail.telefoneContato || ""}
              type="tel"
              onSave={(valor) => salvarCampo("telefoneContato", valor)}
              disabled={!detail.temContato}
              disabledHint="Cadastre o Celular (WhatsApp) primeiro"
            />
          </div>
          <div className={classes.campoPrincipal}>
            <EditableField
              label="Email"
              value={detail.email}
              type="email"
              onSave={(valor) => salvarCampo("email", valor)}
            />
          </div>
          <div className={classes.campoPrincipal}>
            <EditableField
              label="Data Nasc."
              value={detail.dataNascimento ? formatDateOnly(detail.dataNascimento) : null}
              rawValue={detail.dataNascimento || ""}
              type="date"
              onSave={(valor) => salvarCampo("dataNascimento", valor)}
            />
          </div>
        </div>
        <div className={classes.rowEndereco}>
          <div className={classes.campoEndereco}>
            <EditableField
              label="Endereço"
              value={detail.endereco}
              onSave={(valor) => salvarCampo("endereco", valor)}
            />
          </div>
          <div className={classes.campoCidade}>
            <EditableField
              label="Cidade"
              value={detail.cidade}
              onSave={(valor) => salvarCampo("cidade", valor)}
            />
          </div>
        </div>
      </div>

      <Typography variant="subtitle2" className={classes.sectionTitle}>
        Vigentes
      </Typography>
      {detail.vigentes.length ? (
        detail.vigentes.map((policy) => (
          <PolicyLine key={policy.id} policy={policy} onOpenResumo={setResumoAlvo} />
        ))
      ) : (
        <div className={classes.emptySection}>Nenhuma apólice vigente.</div>
      )}

      <Typography variant="subtitle2" className={classes.sectionTitle}>
        Encerradas
      </Typography>
      {detail.encerradas.length ? (
        detail.encerradas.map((policy) => (
          <PolicyLine key={policy.id} policy={policy} onOpenResumo={setResumoAlvo} />
        ))
      ) : (
        <div className={classes.emptySection}>Nenhuma apólice encerrada.</div>
      )}

      <SummaryModal
        open={Boolean(resumoAlvo)}
        policyId={resumoAlvo}
        onClose={() => setResumoAlvo(null)}
      />

      <SetCelularModal
        open={celularModalOpen}
        onClose={() => setCelularModalOpen(false)}
        cpfCnpj={documento}
        initialCelular={detail.telefone}
        initialName={detail.nome}
        onSuccess={() => {
          fetchDetail();
        }}
      />

      <OccurrencesSection documento={documento} policies={detail.vigentes} />
    </div>
  );
};

export default ClientDetailPanel;
